import { FC, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormDialog } from 'components/Dialogs';
import CommonCompanyProfileForm from 'pages/CommonCompanyProfiles/components/CommonCompanyProfileForm';
import {
  EMPTY_FORM_STATE,
  ProfileFormState,
  SaveConflict,
  toWritePayload,
} from 'pages/CommonCompanyProfiles/components/CommonCompanyProfileForm/CommonCompanyProfileForm.props';
import { useConfirmCompanyProfile, useGetCommonProfile, useUpdateCommonProfile } from 'services/hooks/companies';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { EditCommonProfileDialogProps } from './EditCommonProfileDialog.props';

const EditCommonProfileDialog: FC<EditCommonProfileDialogProps> = ({
  open,
  onClose,
  profileId,
  companyId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: profile, isLoading } = useGetCommonProfile(open ? profileId : null);
  const updateMutation = useUpdateCommonProfile();
  const getCompanyInfo = useGetCompanyInfoById();

  const refreshAndClose = async () => {
    if (companyId != null) {
      await getCompanyInfo(companyId);
    }
    onClose();
  };

  const confirmMutation = useConfirmCompanyProfile(refreshAndClose);

  const [formValues, setFormValues] = useState<ProfileFormState>(EMPTY_FORM_STATE);
  const [saveConflicts, setSaveConflicts] = useState<SaveConflict[]>([]);

  useEffect(() => {
    if (profile) {
      setFormValues({
        name: profile.name ?? '',
        cap_iq_id: profile.cap_iq_id ?? '',
        sacra_domain: profile.sacra_domain ?? '',
        zanbato_id: profile.zanbato_id ?? '',
      });
    }
  }, [profile]);

  useEffect(() => {
    if (!open) {
      setFormValues(EMPTY_FORM_STATE);
      setSaveConflicts([]);
    }
  }, [open]);

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ id: profileId, data: toWritePayload(formValues) });
      enqueueSnackbar('Profile updated successfully', { variant: 'success' });
      await refreshAndClose();
    } catch (err) {
      const axiosErr = err as any;
      const errStatus = axiosErr?.response?.status ?? axiosErr?.status;
      const errBody = axiosErr?.response?.data ?? axiosErr?.body;
      if (errStatus === 409 && errBody?.conflicts?.length > 0) {
        setSaveConflicts(errBody.conflicts as SaveConflict[]);
      } else {
        enqueueSnackbar('Failed to save profile. Please try again.', { variant: 'error' });
      }
      throw err;
    }
  };

  const handleConfirm = () => {
    if (companyId != null) {
      confirmMutation.mutate({ companyId });
    }
  };

  const confirmButton = companyId != null ? (
    <Button
      variant="contained"
      color="primary"
      onClick={handleConfirm}
      disabled={isLoading || confirmMutation.isPending}
    >
      Confirm Match
    </Button>
  ) : undefined;

  return (
    <FormDialog
      open={open}
      title="Edit Common Profile"
      onClose={onClose}
      onSave={handleSave}
      isValid={formValues.name.trim().length > 0 && saveConflicts.length === 0}
      isLoading={isLoading || updateMutation.isPending}
      customButton={confirmButton}>
      <CommonCompanyProfileForm
        values={formValues}
        onChange={vals => {
          setSaveConflicts([]);
          setFormValues(vals);
        }}
        isLoading={isLoading || updateMutation.isPending}
        profileId={profileId}
        saveConflicts={saveConflicts}
      />
    </FormDialog>
  );
};

export default EditCommonProfileDialog;
